import { DOCUMENT } from '@angular/common';
import { ApplicationRef, Injectable, inject, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { concat, filter, first, interval } from 'rxjs';

export interface VersionUpdateEvent {
  source: string;
  type: 'NEW_VERSION' | 'NO_UPDATE' | 'ERROR';
  details?: any;
}

const UPDATE_CHECK_INTERVAL_MS = 60000;
const REPROMPT_DELAY_MS = 60000;

@Injectable({ providedIn: 'root' })
export class NgSwService {
  private updates = inject(SwUpdate);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private document = inject<Document>(DOCUMENT);
  private appRef = inject(ApplicationRef);

  private updatePromptShown = false;

  private versionEventSignal = signal<VersionUpdateEvent | null>(null);
  readonly versionEvent = this.versionEventSignal.asReadonly();

  /** Initialize SW logic */
  async init(): Promise<void> {
    if (!this.updates.isEnabled) {
      console.log('Service Worker not enabled.');
      return;
    }

    console.log('Initializing Service Worker logic...');

    this.updates.versionUpdates.pipe(filter(e => e.type === 'VERSION_INSTALLATION_FAILED')).subscribe(e => {
      console.error('SW install failed:', e);
      this.versionEventSignal.set({ source: 'sw', type: 'ERROR', details: e });
    });

    // A new version has been fully downloaded - found by the polling below or by the SW's own check on page load
    this.updates.versionUpdates.pipe(filter(e => e.type === 'VERSION_READY')).subscribe(() => {
      this.versionEventSignal.set({ source: 'sw', type: 'NEW_VERSION' });
      this.promptUserToReload('sw-event');
    });

    // The SW can no longer serve every file of the version this tab is running (e.g. the browser evicted its
    // cache), so lazy routes would start failing. Move the tab onto the current version.
    this.updates.unrecoverable.subscribe(e => {
      console.error('SW unrecoverable state:', e.reason);
      this.document.location.reload();
    });

    // Poll only once the app is stable. A timer started earlier keeps zone.js from ever reporting the app
    // stable, which holds back the SW registration ('registerWhenStable') until its timeout.
    concat(this.appRef.isStable.pipe(first(isStable => isStable)), interval(UPDATE_CHECK_INTERVAL_MS)).subscribe(() => this.checkForNewVersion('polling'));
  }

  /** Force a manual version check */
  async checkForNewVersion(source: string): Promise<void> {
    try {
      const isNewVersion = await this.updates.checkForUpdate();

      if (isNewVersion) {
        console.log(`New version found via ${source}.`);
        this.versionEventSignal.set({ source, type: 'NEW_VERSION' });
        this.promptUserToReload(source);
      } else {
        this.versionEventSignal.set({ source, type: 'NO_UPDATE' });
      }
    } catch (error) {
      console.warn('SW - Error checking new version:', error);
      this.versionEventSignal.set({ source, type: 'ERROR', details: error });
    }
  }

  /** Prompt user with snackbar to reload */
  private promptUserToReload(source: string): void {
    // Avoid duplicate prompts
    if (this.updatePromptShown) {
      return;
    }
    this.updatePromptShown = true;
    console.log(`New version detected via ${source}.`);

    const snackRef = this.snackBar.open(this.translate.instant('COMMON.NEW_APP_VERSION_AVAILABLE'), this.translate.instant('COMMON.RELOAD'), {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['update-snackbar']
    });

    snackRef.onAction().subscribe(() => this.activateAndReload(source));

    snackRef.afterDismissed().subscribe(info => {
      if (!info.dismissedByAction) {
        // Dismissed, or replaced by another snackbar. Ask again later - never reload without the user's
        // go-ahead, they may be in the middle of a form.
        this.updatePromptShown = false;
        console.log('User ignored update prompt. Will re-prompt in 60s.');
        setTimeout(() => this.promptUserToReload(source), REPROMPT_DELAY_MS);
      }
    });
  }

  /** Switch to the new version and reload the page onto it */
  private async activateAndReload(source: string): Promise<void> {
    try {
      await this.updates.activateUpdate();
    } catch (error) {
      // The reload alone still picks up the latest version
      console.warn('SW - activateUpdate failed, reloading anyway:', error);
    }
    console.log('Reloading due to new version (source:', source, ')');
    this.document.location.reload();
  }
}
